import axios from 'axios';
import { serialize } from 'object-to-formdata';
import fileDownload from 'js-file-download';
import moment from 'moment/moment';
import _ from 'lodash';
import apiConfig from '../config';
import { socketDestroy, healthPlanSocketInit } from '../store/actions/chat';
import accountHelpers from '../utils/accountHelpers';
import { rightChatSocketDestroy, socketInit } from '../store/actions/rightBar';
import { getUnassignedPhysicians } from '../store/actions/dashboard';

const { API_URL } = apiConfig;

const tokenRefresh = async () => {
  const refreshToken = accountHelpers.getToken('refreshToken');
  const { email, username } = accountHelpers.getUser();

  if (refreshToken && (email || username)) {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/user/token/refresh`,
        {
          username: email || username,
          refreshToken,
        },
        { headers: { Authorization: `Bearer ${refreshToken}` } },
      );

      if (data?.token) {
        accountHelpers.setToken(data.token);

        const userId = accountHelpers.getDecodedToken()?.id;
        const payload = await Api.getAccount(userId);
        accountHelpers.setUser(payload.data.data.response[0]);

        if (accountHelpers.isLocked()) {
          localStorage.setItem(
            'error',
            `Account locked due to too many wrong password tries. Try agian after ${accountHelpers.getLockDate()
              .format('YYYY-MM-DD HH:mm:ss')}`,
          );

          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        } else if (!accountHelpers.isActive()) {
          accountHelpers.removeLocalStorage();

          localStorage.setItem(
            'error',
            'This account is not active. Please ask an administrator for more information.',
          );

          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        } else {
          window.store.dispatch(socketInit(data.token));
          // window.store.dispatch(healthPlanSocketInit(data.token));
        }
      }
    } catch (e) {
      accountHelpers.removeLocalStorage();
      window.location.href = '/login';
    }
  } else {
    accountHelpers.removeLocalStorage();
    window.location.href = '/login';
  }
};

const api = axios.create({
  headers: {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'skip-browser-warning',
  },
});

api.interceptors.request.use(async (config) => {
  let token = accountHelpers.getToken();

  if (token) {
    const expiresAt = accountHelpers.getDecodedToken()?.exp;

    if (new Date(expiresAt * 1000) <= new Date()) {
      // window.store.dispatch(socketDestroy());
      window.store.dispatch(rightChatSocketDestroy());
      await tokenRefresh();
      token = accountHelpers.getToken();
    }
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
}, (error) => Promise.reject(error));

api.interceptors.response.use((response) => response, (error) => {
  if (error.response && error.response.status === 401) {
    if (window.location.pathname !== '/login') {
      localStorage.removeItem('token');

      window.location.href = '/login';
    }
  }

  return Promise.reject(error);
});

const apiTokens = {};

class Api {
  static cancel(methodName) {
    if (apiTokens[methodName]) {
      apiTokens[methodName]();
    }
  }

  static cancelToken(methodName) {
    return new axios.CancelToken((token) => {
      apiTokens[methodName] = token;
    });
  }

  static login(data) {
    return api.post(`${API_URL}/api/user/login`, data);
  }

  static loginAdminToHealth({ id }) {
    return api.post(`${API_URL}/api/user/login/admin`, { id });
  }

  static changePassword(data) {
    return api.post(`${API_URL}/api/account/chpass`, data);
  }

  static setNewPassword(data) {
    return api.post(`${API_URL}/api/account/respass`, data);
  }

  static getAccount(userId) {
    return api(`${API_URL}/api/user/search?userId=${userId}`);
  }

  static getUserList({
    role, limit, page = 1, email, username, name, searcherId, userRole,
    userId, groupName, healthId, access, groupId,
  }) {
    return api(`${API_URL}/api/user/search`, {
      params: {
        role,
        limit,
        page,
        email,
        username,
        name,
        searcherId,
        userRole,
        userId,
        groupName,
        healthId,
        groupId,
        access,
      },
    });
  }

  static getDashboardStats({ start, end, rep }) {
    return api(`${API_URL}/api/dashboards`, {
      params: { start, end, rep },
    });
  }

  static getDashboardChart({
    start, end, range, rep,
  }) {
    return api(`${API_URL}/api/dashboards/chart`, {
      params: {
        start, end, range, rep,
      },
    });
  }

  static getDashboardGrowthBySpecializations({ productId, state, rep }) {
    return api(`${API_URL}/api/dashboards/report/growth`, {
      params: {
        productId, state, rep,
      },
    });
  }

  static async getDashboardSyncPakVsSortPak({
    start, end, state, key, pouch, health, download,
  }) {
    const {
      data, totalSortPak, totalSyncPak,
    } = await api(`${API_URL}/api/dashboards/report/pouch`, {
      params: {
        start, end, state, key, pouch, health, download,
      },
      responseType: download ? 'blob' : 'json',
    });

    if (download) {
      fileDownload(data, 'SyncPak_vs_SortPak.xlsx');
    }

    return { data, totalSortPak, totalSyncPak };
  }

  static getDashboardRepScorecard({
    start, end, page, limit,
  }) {
    return api(`${API_URL}/api/dashboards/report/rep`, {
      params: {
        start, end, page, limit,
      },
    });
  }

  static async getDashboardUserWorkflow({ start, end, download }) {
    const { data } = await api(`${API_URL}/api/dashboards/report/workflow`, {
      params: {
        start,
        end,
        download,
      },
      responseType: download ? 'blob' : 'json',
    });

    if (download) {
      fileDownload(data, 'Report.xlsx');
    }

    return data;
  }

  static async getDashboardNoteCount({ start, end }) {
    const { data } = await api(`${API_URL}/api/dashboards/report/notes`, { params: { start, end } });

    return data;
  }

  static getDashboardNewPhysician({
    start, end, page, limit, productId, specialization,
  }) {
    return api(`${API_URL}/api/dashboards/report/new-physician`, {
      params: {
        start, end, page, limit, productId, specialization,
      },
    });
  }

  static searchProduct(search) {
    return api(`${API_URL}/api/products/search`, {
      params: {
        search,
        limit: 100,
      },
    });
  }

  static updateProduct(data) {
    return api.put(`${API_URL}/api/products/update`, data);
  }

  static getTopMedications({
    limit, page, zip, productId, specialization, end, start,
  }) {
    return api(`${API_URL}/api/dashboards/report/medication`, {
      params: {
        limit, page, zip, productId, specialization, end, start,
      },
    });
  }

  static getTopCities({
    limit, page, rep, zip, productId, specialization, end, start,
  }) {
    return api(`${API_URL}/api/dashboards/report/city`, {
      params: {
        limit, page, rep, zip, productId, specialization, end, start,
      },
    });
  }

  static getTopState({
    limit, page, rep, zip, productId, specialization, end, start,
  }) {
    return api(`${API_URL}/api/dashboards/report/state`, {
      params: {
        limit, page, rep, zip, productId, specialization, end, start,
      },
    });
  }

  static getTopSpecializations({
    limit, page, rep, zip, productId, end, start,
  }) {
    return api(`${API_URL}/api/dashboards/report/specialization`, {
      params: {
        limit, page, rep, zip, productId, end, start,
      },
    });
  }

  static getTopTopCopayNetworks({
    limit, page, rep, zip, productId, specialization, end, start,
  }) {
    return api(`${API_URL}/api/dashboards/report/copay`, {
      params: {
        limit, page, rep, zip, productId, specialization, end, start,
      },
    });
  }

  static getTopPayors({
    limit, page, rep, zip, productId, specialization, end, start,
  }) {
    return api(`${API_URL}/api/dashboards/report/payor`, {
      params: {
        limit, page, rep, zip, productId, specialization, end, start,
      },
    });
  }

  static getTopHubs({
    limit, page, rep, zip, productId, specialization, end, start,
  }) {
    return api(`${API_URL}/api/dashboards/report/hub`, {
      params: {
        limit, page, rep, zip, productId, specialization, end, start,
      },
    });
  }

  static getTopPhysicians({
    limit, page, rep, zip, productId, specialization, end, start,
  }) {
    return api(`${API_URL}/api/dashboards/report/physician`, {
      params: {
        limit, page, rep, zip, productId, specialization, end, start,
      },
    });
  }

  static getUnassignedPhysicians({
    limit, page, rep, zip, productId, specialization, end, start,
  }) {
    return api(`${API_URL}/api/dashboards/report/phys-blank`, {
      params: {
        limit, page, rep, zip, productId, specialization, end, start,
      },
    });
  }

  static getTopGroups({
    limit, page, rep, zip, productId, specialization, end, start,
  }) {
    return api(`${API_URL}/api/dashboards/report/group`, {
      params: {
        limit, page, rep, zip, productId, specialization, end, start,
      },
    });
  }

  static getScripts({
    status, location, salesCode, homeCare, textSearch, limit, rep, assign,
    specialization, userId, page, patientId, scriptId, physicianId, healthId, patientLocation, pouch, download,
  }) {
    return api(`${API_URL}/api/scripts/search`, {
      params: {
        status,
        location,
        salesCode,
        homeCare,
        textSearch,
        limit,
        rep,
        specialization,
        userId,
        page,
        patientId,
        scriptId,
        physicianId,
        assign,
        healthId,
        patientLocation,
        pouch,
        download,
      },
    });
  }

  static addScript(data) {
    return api.post(`${API_URL}/api/scripts/add`, data);
  }

  static deleteScript(id) {
    return api.delete(`${API_URL}/api/scripts/delete/${id}`);
  }

  static updateScript(data) {
    return api.put(`${API_URL}/api/scripts/update`, data);
  }

  static updateScriptStatuses(data) {
    return api.post(`${API_URL}/api/scripts/statuses/add`, data);
  }

  static getMessages({ page }) {
    return api(`${API_URL}/api/chat`, {
      params: { page },
    });
  }

  static setMessage({ message, mention, receiverId = null }) {
    debugger;
    return api.post(`${API_URL}/api/chat`, { message, mention, receiverId });
  }

  static getNotifications(page) {
    return api(`${API_URL}/api/chat/note`, {
      params: { page, limit: 20 },
    });
  }

  static getNotificationCount() {
    return api(`${API_URL}/api/chat/note`);
  }

  static getPatients({
    page = 1, limit, userId, name, dob, address, phone, patientId,
  }) {
    return api(`${API_URL}/api/patients/search`, {
      params: {
        page, limit, userId, name, dob, address, phone, patientId,
      },
    });
  }

  static sentNewUser({
    name, email, username, role, active, lastName, dob, zip, phone, medicalId,
  }) {
    return api.post(`${API_URL}/api/account/register`, {
      name, email, username, role, active, lastName, dob, zip, phone, medicalId,
    });
  }

  static sentEditUser({
    name, email, username, active, id, role, lastName, dob, lockedDateTimeUTC, lockedCount,
  }) {
    return api.put(`${API_URL}/api/user/update`, {
      name, email, username, active, id, role, lastName, dob, lockedDateTimeUTC, lockedCount,
    });
  }

  static getReqChangePassword({ userId, targetUserId }) {
    return api.get(`${API_URL}/api/account/respass`, {
      params: {
        userId, targetUserId,
      },
    });
  }

  static sentEditProfileUser(data) {
    return api.put(`${API_URL}/api/user/update`, serialize(data));
  }

  static mergeScripts({ oldPatientId, patientId }) {
    return api.put(`${API_URL}/api/scripts/merge`, { oldPatientId, patientId });
  }

  static mergePhysicianScripts({ oldPhysicianId, physicianId, userId }) {
    return api.put(`${API_URL}/api/scripts/mergephys`, { oldPhysicianId, physicianId, userId });
  }

  static deletePatient({ patientId, userId }) {
    return api.delete(`${API_URL}/api/patients/delete`, { data: { patientId, userId } });
  }

  static createPatient(data) {
    return api.post(`${API_URL}/api/patients/add`, data);
  }

  static updatePatient(data) {
    return api.put(`${API_URL}/api/patients/update`, data);
  }

  static getPastInsurance({ patientId }) {
    return api.get(`${API_URL}/api/pastInsurance/search`, {
      params: { patientId },
    });
  }

  static getPatientAttachments({ patientId }) {
    return api.get(`${API_URL}/api/patientAttachments/search`, {
      params: { patientId },
    });
  }

  static sendInsuranceFile({
    attachmentFile, fileName, patientId, attachmentId, attachedBy, scriptId, faxes, formData,
  }) {
    return api.put(`${API_URL}/api/sthree/put`, formData || serialize({ attachmentFile }), {
      params: {
        fileName, patientId, attachmentId, attachedBy, scriptId, faxes,
      },
    });
  }

  static uploadInsuranceFile({
    type, attachmentFile, fileName, patientId, userId, attachedBy,
  }) {
    return api.post(`${API_URL}/api/patientAttachments/upload`, serialize({ attachmentFile }), {
      params: {
        type, fileName, patientId, userId, attachedBy,
      },
    });
  }

  static getFile({
    fileName, patientId, userId, attachmentId, scriptId,
  }, onDownloadProgress) {
    return api.get(`${API_URL}/api/sthree/get`, {
      params: {
        fileName, patientId, userId, attachmentId, scriptId,
      },
      onDownloadProgress,
    });
  }

  static getPatientsNode({ patientId, userId }) {
    return api.get(`${API_URL}/api/patients/notes/search`, {
      params: { patientId, userId },
    });
  }

  static getRefills({
    status, textSearch, rep, spec, page, limit, userId,
  }) {
    return api.get(`${API_URL}/api/scripts/refills/search`, {
      params: {
        status, textSearch, rep, spec, page, limit, userId,
      },
    });
  }

  static getScriptsNotes({
    scriptId, userId,
  }) {
    return api.get(`${API_URL}/api/scripts/notes/search`, {
      params: { scriptId, userId },
    });
  }

  static addScriptNote(data) {
    return api.post(`${API_URL}/api/scripts/notes/add`, data);
  }

  static addScriptAttachmentCopy(data) {
    return api.post(`${API_URL}/api/attachments/copy`, data);
  }

  static addScriptNoteCopy(data) {
    return api.post(`${API_URL}/api/scripts/notes/copy`, data);
  }

  static getScriptStatus({ ScriptId }) {
    return api.get(`${API_URL}/api/scripts/statuses/search/`, {
      params: { ScriptId },
    });
  }

  static getScriptPayments({ scriptId }) {
    return api.get(`${API_URL}/api/scripts/payments/search`, {
      params: { scriptId },
    });
  }

  static getScriptPaymentsPdf({ fileName, receiptId, userId }, onDownloadProgress) {
    return api.get(`${API_URL}/api/sthree/get`, {
      params: { fileName, receiptId, userId },
      onDownloadProgress,
    });
  }

  static getScriptAttachments({ ScriptId, userId }) {
    return api.get(`${API_URL}/api/attachments/search`, {
      params: { ScriptId, userId },
    });
  }

  static uploadAttachmentFile({
    userId, scriptId, fileName, type, attachedBy, attachmentFile,
  }) {
    return api.post(`${API_URL}/api/attachments/upload`, serialize({ attachmentFile }), {
      params: {
        userId, scriptId, fileName, type, attachedBy,
      },
    });
  }

  static scriptCheckout({ userId, statuses, patientId }) {
    return api.post(`${API_URL}/api/scripts/checkout`, { userId, statuses, patientId });
  }

  static paymentCharge(data) {
    return api.post(`${API_URL}/api/scripts/payments/charge/card`, data);
  }

  static paymentChargeCustomer(data) {
    return api.post(`${API_URL}/api/scripts/payments/charge/customer`, data);
  }

  static uploadToSthree(data, fileName, receiptId) {
    return api.put(`${API_URL}/api/sthree/put?fileName=${fileName}&receiptId=${receiptId}`, data);
  }

  static getPhysicians({
    searchSpec, phone, address, searchGroup, name, limit, page, userId, physicianId, revenue, group, physGroup,
  }) {
    return api.get(`${API_URL}/api/physicians/search`, {
      params: {
        searchSpec, phone, address, searchGroup, name, limit, page, userId, physicianId, revenue, group, physGroup,
      },
    });
  }

  static createPhysician(data) {
    return api.post(`${API_URL}/api/physicians/add`, data);
  }

  static updatePhysician(data) {
    return api.put(`${API_URL}/api/physicians/update`, data);
  }

  static deletePhysician({ physicianId }) {
    return api.delete(`${API_URL}/api/physicians/delete`, { data: { physicianId } });
  }

  static getPhysicianNotes({ physicianId }) {
    return api.get(`${API_URL}/api/visits/notes/search`, {
      params: { physicianId },
    });
  }

  static getPhysicianVisits({ physicianId, group }) {
    return api.get(`${API_URL}/api/visits/search`, {
      params: { physicianId, group },
    });
  }

  static addVisit(data) {
    return api.post(`${API_URL}/api/visits/add`, data);
  }

  static editVisit(data) {
    return api.put(`${API_URL}/api/visits/update`, data);
  }

  static deleteVisit(id) {
    return api.delete(`${API_URL}/api/visits/delete`, { data: { id } });
  }

  static addPhysicianNote(data) {
    return api.post(`${API_URL}/api/visits/notes/add`, data);
  }

  static addPhysicianAccess(data) {
    return api.post(`${API_URL}/api/user/userPhysicians`, data);
  }

  static deletePhysicianAccess(data) {
    return api.delete(`${API_URL}/api/user/userPhysicians/delete`, { data });
  }

  static getPhysicianGroupNotes({ group }) {
    return api.get(`${API_URL}/api/groupNotes/search`, {
      params: { group },
    });
  }

  static addPhysicianGroupNote(data) {
    return api.post(`${API_URL}/api/groupNotes/add`, data);
  }

  static getPhysicianGroupUsers({ group }) {
    return api.get(`${API_URL}/api/physicians/tab`, {
      params: { group },
    });
  }

  static getProducts({
    page, limit, search, productId, otc,
  }) {
    return api.get(`${API_URL}/api/products/search`, {
      params: {
        page, limit, search, productId, otc,
      },
    });
  }

  static addProduct(data) {
    return api.post(`${API_URL}/api/products/add`, data);
  }

  static editProduct(data) {
    return api.put(`${API_URL}/api/products/update`, data);
  }

  static productUploadXLSXFile({ sheet }) {
    return api.post(`${API_URL}/api/products/upload`, serialize({ sheet }));
  }

  static productUploadZipFile({ zip }) {
    return api.post(`${API_URL}/api/products/image`, serialize({ zip }));
  }

  static async getInventory({
    productId, userId, start, end, download,
  }) {
    const { data, headers } = await api.get(`${API_URL}/api/products/inventory/search`, {
      params: {
        productId, userId, start, end, download,
      },
      responseType: download ? 'blob' : 'json',
    });

    if (download) {
      const [, fileName] = (headers['Content-Disposition'] || '').match(/\s+filename=(.+)$/) || ['', 'inventory-tracker.xlsx'];

      fileDownload(data, fileName);
    }

    return data;
  }

  static addProductOrder(data) {
    return api.post(`${API_URL}/api/products/orders/add`, data);
  }

  static updateProductOrder(data) {
    return api.put(`${API_URL}/api/products/orders/update`, data);
  }

  static getProductOrder({ orderId, batchId }) {
    return api.get(`${API_URL}/api/products/orders/search`, {
      params: { orderId, batchId },
    });
  }

  static addAdjustProduct(data) {
    return api.post(`${API_URL}/api/products/adjustments/add`, data);
  }

  static getAdjustProduct({ adjustId, batchId }) {
    return api.get(`${API_URL}/api/products/adjustments/search`, {
      params: { adjustId, batchId },
    });
  }

  static updateAdjustProduct(data) {
    return api.put(`${API_URL}/api/products/adjustments/update`, data);
  }

  static getHealth({
    userId, name, hubId, address, phone, page, limit, healthId, groupId, revenue,
  }) {
    return api.get(`${API_URL}/api/health/search`, {
      params: {
        userId, name, hubId, address, phone, page, limit, healthId, groupId, revenue,
      },
    });
  }

  static addHealth(data) {
    return api.post(`${API_URL}/api/health/add`, data);
  }

  static updateHealth(data) {
    return api.put(`${API_URL}/api/health/update`, data);
  }

  static sendHealthGroup(data) {
    return api.post(`${API_URL}/api/health/group`, data);
  }

  static deleteHealthGroup(data) {
    return api.delete(`${API_URL}/api/health/group`, { data: { ...data } });
  }

  static assignTo({ assign, scriptId }) {
    return api.get(`${API_URL}/api/scripts/assign`, {
      params: { assign, scriptId },
    });
  }

  static uploadRefileFax(fileName, faxNumber, formData) {
    return api.post(`${API_URL}/api/faxes/upload?faxNumber=${faxNumber}&fileName=${fileName}`, formData);
  }

  static scriptFaxUpload(data) {
    return api.put(`${API_URL}/api/scripts/fax`, data);
  }

  static async getSingleReport(body, { page, limit }, name = 'file.xlsx') {
    const { data, headers } = await api.post(`${API_URL}/api/dashboards/report/single`, body, {
      params: { page, limit },
      responseType: body.download ? 'blob' : 'json',
    });

    if (body.download) {
      const [, fileName] = (headers['content-disposition'] || '').match(/\s+filename=(.+)$/) || ['', name];

      fileDownload(data, fileName);
    }

    return data;
  }

  static deleteCard(data) {
    return api.delete(`${API_URL}/api/patients/profile`, { data });
  }

  static createCard(data) {
    return api.post(`${API_URL}/api/patients/profile`, data);
  }

  static mageNotePhysician(data) {
    return api.put(`${API_URL}/api/visits/notes/merge`, data);
  }

  static sendDeliveryPrice(data) {
    return api.post(`${API_URL}/api/delivery/price`, data);
  }

  static possiblePersonsCSVImportFile(file) {
    return api.post(`${API_URL}/api/persons/header`, serialize({ file }));
  }

  static possiblePersonsCSVUploadFile(data) {
    return api.post(`${API_URL}/api/persons/upload`, serialize(data));
  }

  static createPersons(data) {
    return api.post(`${API_URL}/api/persons`, data);
  }

  static getAllPersonsList() {
    return api.get(`${API_URL}/api/persons/data/first`);
  }

  static getPersonsList({ status, page, agent }) {
    return api.get(`${API_URL}/api/persons`, {
      params: {
        status, page, limit: 30, agent,
      },
    });
  }

  static getSinglePersons(id) {
    return api.get(`${API_URL}/api/persons/${id}`);
  }

  static changePersonStatus(data) {
    return api.put(`${API_URL}/api/persons/status/${data.id}`, {
      status: data.status,
      from: _.startCase(data.data.currentItem.parent),
      to: _.startCase(data.data.item.parent),
    });
  }

  static googleOauth() {
    return api.get(`${API_URL}/api/persons/calendar/auth`);
  }

  static googleOauthToken({ code, id }) {
    return api.get(`${API_URL}/api/persons/calendar/auth/token?code=${code}&id=${id}`);
  }

  static updatePerson(data) {
    return api.put(`${API_URL}/api/persons/data/${data.id}`, data);
  }

  static getPersonNotes(personId) {
    return api.get(`${API_URL}/api/persons/notes/${personId}`);
  }

  static assignToRequest(data) {
    return api.put(`${API_URL}/api/persons/responsible`, data);
  }

  static addActivity(data) {
    return api.post(`${API_URL}/api/persons/activity`, data);
  }

  static getActivity({
    id, start, end, page,
  }) {
    return api.get(`${API_URL}/api/persons/activities/${id}`, {
      params: { start, end, page },
    });
  }

  static editActivity(data) {
    return api.put(`${API_URL}/api/persons/activity/${data.id}`, data);
  }

  static markAsDoneActivity({ status, id }) {
    return api.put(`${API_URL}/api/persons/task/${id}?status=${status}`);
  }

  static getChangelog({ id, page }) {
    return api.get(`${API_URL}/api/persons/logs/${id}`, {
      params: { page },
    });
  }

  static getTotalContactsImported({ start, end, healthPlanId }) {
    return api.get(`${API_URL}/api/persons/statistic/import`, {
      params: {
        start,
        end: end
          ? moment(end).add(1, 'day').format('YYYY-MM-DD')
          : moment(start).add(1, 'day').format('YYYY-MM-DD'),
        healthPlanId,
      },
    });
  }

  static getContacted({ start, healthPlanId }) {
    return api.get(`${API_URL}/api/persons/statistic/contacted`, {
      params: { start, healthPlanId },
    });
  }

  static getFollowUp({
    end, start, healthPlanId, status,
  }) {
    return api.get(`${API_URL}/api/persons/statistic/follow`, {
      params: {
        end: end
          ? moment(end).add(1, 'day').format('YYYY-MM-DD')
          : moment(start).add(1, 'day').format('YYYY-MM-DD'),
        start,
        healthPlanId,
        status,
      },
    });
  }

  static getActivitiesCompleted({ end, start, healthPlanId }) {
    return api.get(`${API_URL}/api/persons/statistic/activity`, {
      params: {
        end: end
          ? moment(end).add(1, 'day').format('YYYY-MM-DD')
          : moment(start).add(1, 'day').format('YYYY-MM-DD'),
        start,
        healthPlanId,
      },
    });
  }

  static getPipelineProgress({ end, start, healthPlanId }) {
    return api.get(`${API_URL}/api/persons/statistic/progress`, {
      params: {
        end: end
          ? moment(end).add(1, 'day').format('YYYY-MM-DD')
          : moment(start).add(1, 'day').format('YYYY-MM-DD'),
        start,
        healthPlanId,
      },
    });
  }

  static getChatHealthPlanList({ page, order, orderBy }) {
    return api.get(`${API_URL}/api/health/chat/health-plans`, {
      params: { page, order, orderBy },
    });
  }

  static getChatAssignToList({
    page, order, orderBy, includeAgents,
  }) {
    return api.get(`${API_URL}/api/health/chat/health-plan/assignable/users`, {
      params: {
        page, order, orderBy, includeAgents,
      },
    });
  }

  static assignUserToHealthPlan(data) {
    return api.post(`${API_URL}/api/health/chat/health-plan/assign/user`, data);
  }

  static getChatAssignedUser(params) {
    return api.get(`${API_URL}/api/health/chat/health-plan/assigned/user`, { params });
  }
  // static getChatSelectedHealthPlans({ page, order, orderBy }) {
  //   return api.get(`${chatURL}/api/health/chat/health-plans`, {
  //     params: { page, order, orderBy },
  //   });
  // }

  // static addChatSelectedHealthPlans(id) {
  //   return api.get(`${chatURL}/api/health/chat/health-plans`, {
  //     params: { id },
  //   });
  // }

  static getChatHealthPlanMembersList({
    page, order = 'desc', orderBy = 'roomDate', id, s,
  }) {
    return api.get(`${API_URL}/api/health/chat/health-plan/${id}/members`, {
      params: {
        page, order, orderBy, s,
      },
    });
  }

  static setHealthPlanRoom(data) {
    this.cancel('setHealthPlanRoom');

    return api.post(
      `${API_URL}/api/health/chat/health-plan/set/room`,
      data,
      { cancelToken: this.cancelToken('setHealthPlanRoom') },
    );
  }

  static getHealthPlanChatMessages({
    roomId, messageId, mode, excludeMsg = 1,
  }) {
    this.cancel('getHealthPlanChatMessages');

    return api.get(`${API_URL}/api/health/chat/health-plan/room/messages`, {
      cancelToken: this.cancelToken('getHealthPlanChatMessages'),
      params: {
        roomId, messageId, mode, excludeMsg,
      },
    });
  }

  static sendHealthPlanChatMessage(data) {
    return api.post(`${API_URL}/api/health/chat/send`, data);
  }

  static getMarkedHealthPlans({ page = 1, order = 'desc', orderBy = 'active' }) {
    return api.get(`${API_URL}/api/health/chat/health-plan/marked`, {
      params: { page, order, orderBy },
    });
  }

  static markHealthPlan(id) {
    return api.post(`${API_URL}/api/health/chat/health-plan/mark`, { healthPlanId: id });
  }

  static markHealthPlanActive(id) {
    return api.post(`${API_URL}/api/health/chat/health-plan/mark/active`, { healthPlanId: id });
  }

  static markHealthPlanChatSeen(roomId) {
    return api.get(`${API_URL}/api/health/chat/mark/${roomId}/seen`);
  }

  static searchMessage(data) {
    return api.post(
      `${API_URL}/api/health/chat/health-plan/search/room/message`,
      { ...data, page: 1 },
    );
  }

  static getMessageListByMessageId(messageId) {
    return api.get(`${API_URL}/api/health/chat/health-plan/searched/room/message/${messageId}`);
  }

  static getUnseenCount() {
    return api.get(`${API_URL}/api/health/chat/health-plan/unseen/count`);
  }

  static getRoomData(roomId) {
    return api.get(`${API_URL}/api/health/chat/health-plan/room/${roomId}`);
  }

  static fileUpload(file, roomId) {
    return api.post(`${API_URL}/api/health/chat/file/upload`, serialize({ file, roomId }));
  }

  static getHealthPlanRoom() {
    return api.get(`${API_URL}/api/health/chat/health-plan/room/patient`);
  }

  // ANALYTICS

  static getMessagesCount({ startDate, endDate, healthPlanId }) {
    return api.get(
      `${API_URL}/api/health/chat/statistics/messages/count`,
      {
        params: { startDate, endDate, healthPlanId },
      },
    );
  }

  static getAnswersCount({ startDate, endDate, healthPlanId }) {
    return api.get(
      `${API_URL}/api/health/chat/statistics/answers/count`,
      {
        params: { startDate, endDate, healthPlanId },
      },
    );
  }

  static getResponseAverageTime({ startDate, endDate, healthPlanId }) {
    return api.get(
      `${API_URL}/api/health/chat/statistics/response/average/time`,
      {
        params: { startDate, endDate, healthPlanId },
      },
    );
  }

  static getMessageVolume({ startDate, endDate, healthPlanId }) {
    return api.get(
      `${API_URL}/api/health/chat/statistics/message/volume/count`,
      {
        params: { startDate, endDate, healthPlanId },
      },
    );
  }

  static getActiveUsers({ startDate, endDate, healthPlanId }) {
    return api.get(
      `${API_URL}/api/health/chat/statistics/top/patients`,
      {
        params: { startDate, endDate, healthPlanId },
      },
    );
  }

  static getActiveAgents({ startDate, endDate, healthPlanId }) {
    return api.get(
      `${API_URL}/api/health/chat/statistics/top/agents`,
      {
        params: { startDate, endDate, healthPlanId },
      },
    );
  }

  static sendRefund(data) {
    return api.post(`${API_URL}/api/scripts/payments/refund/customer`, data);
  }

  static makeCall(data) {
    return api.post(`${API_URL}/api/goto/create-call-queue`, data);
  }

  static hangupCall(data) {
    return api.post(`${API_URL}/api/goto/hangup`, data);
  }
}

export default Api;
